import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConsultsList,
  ConsultsMetaData,
  ConsultForm,
  ConsultView,
  ConsultCancelMetaData,
  ConsultCancelReason,
} from '../services/ConsultsService';
import {
  getConsultCancelMetaData,
  getConsultDetailsById,
  getConsultsList,
  getConsultsMetaData,
  getConsultViewDetailsById,
  getIntakeFormForConsult,
  saveCancelConsult,
  saveOrUpdateConsultsForm,
} from '../services/ConsultsService';
import type { DynamicForm } from '@dynamicForm/FieldConfig';
import { REFRESH_INTERVAL } from '@qler-view/shared_lib/src/shared/constants/CommanConstants';

export interface ConsultsListQueryPerams {
  userId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
  facilityId?: number[];
  followUpId?: number[];
}

export interface ConsultResponse {
  id: number;
  code: number;
  message: string;
  status: string;
}

export const useGetConsultsList = ({
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  statusId,
  facilityId,
  followUpId,
}: ConsultsListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<ConsultsList>({
    queryKey: [
      'ConsultsListResponse',
      page,
      size,
      sortField,
      sortType,
      searchTerm,
      statusId,
      facilityId,
      followUpId,
    ],
    queryFn: async () => {
      const data = await getConsultsList({
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        statusId,
        facilityId,
        followUpId,
      });
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchInterval: REFRESH_INTERVAL,
  });

  return {
    ConsultsListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetConsultsMetaData = (userId: string, consultId: number) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ConsultsMetaData>({
      queryKey: ['ConsultsMetaData', userId, consultId],
      queryFn: async () => {
        const data = await getConsultsMetaData(userId, consultId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    ConsultsMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetIntakeFormForConsult = (
  userId: string,
  facilityId: number,
  consultId?: number
) => {
  const { data, isLoading, isError, error, refetch } = useQuery<DynamicForm>({
    queryKey: ['DynamicIntakeForm', userId, facilityId, consultId],

    queryFn: async () => {
      if (consultId !== undefined) {
        return getIntakeFormForConsult(userId, facilityId, consultId);
      }
      return getIntakeFormForConsult(userId, facilityId); // no consultId
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!facilityId,
  });

  return {
    DynamicIntakeForm: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateConsult = (
  onSuccess: (response: ConsultResponse) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ConsultResponse,
    Error,
    ConsultForm & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateConsultsForm(userId, action, payload);
      return data;
    },

    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['ConsultsListResponse'],
      });
      onSuccess(response);
    },
    onError: (error) => {
      console.error('Error saving or updating consult:', error);
    },
  });
};

export const useGetConsultDetailsById = (userId: string, consultId: number) => {
  const { data, isLoading, isPending, isError, error, refetch } =
    useQuery<ConsultForm>({
      queryKey: ['ConsultDetailsById', userId, consultId],
      queryFn: async () => {
        const data = await getConsultDetailsById(userId, consultId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!consultId,
    });

  return {
    ConsultDetailsById: data,
    isLoading,
    isPending,
    isError,
    error,
    refetch,
  };
};

export const useGetConsultViewDetailsById = (
  userId: string,
  consultId: number
) => {
  const { data, isLoading, isError, error, refetch } = useQuery<ConsultView>({
    queryKey: ['ConsultViewDetailsById', userId, consultId],
    queryFn: async () => {
      const data = await getConsultViewDetailsById(userId, consultId);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!consultId,
  });

  return {
    ConsultViewDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetConsultCancelMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ConsultCancelMetaData>({
      queryKey: ['ConsultCancelMetaData', userId],
      queryFn: async () => {
        const data = await getConsultCancelMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: false,
    });

  return {
    ConsultCancelMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveCancelConsult = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    ConsultCancelReason,
    Error,
    ConsultCancelReason & { userId: string; consultId: number }
  >({
    mutationFn: async ({ userId, consultId, ...payload }) => {
      const data = await saveCancelConsult(userId, consultId, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ConsultsListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving consult cancel reason:', error);
    },
  });
};
